import { TICKETS_URL } from "../config/api";

export interface IssuedTicket {
    // Short code for support (ST-XXXX-XXXX); the QR is what the door checks
    code: string;
    stageName: string;
    unitPrice: number;
    unitServiceFee: number;
    qrPayload: string;
    // The QR as a PNG data URI, only inside this response (never published at a URL)
    qrImage: string;
}

export interface PurchasedTickets {
    reference: string;
    eventName: string;
    eventStart?: string;
    eventEnd?: string;
    eventLocation?: string;
    organizerName?: string;
    buyerEmail: string;
    currency: string;
    subtotal: number;
    serviceFee: number;
    total: number;
    refundContactEmail?: string;
    refundContactPhone?: string;
    tickets: IssuedTicket[];
}

export type TicketsViewResult =
    | { status: 'SHOWN'; tickets: PurchasedTickets }
    // Payment confirmed, tickets still being issued: ask again in a few seconds (the view is not consumed)
    | { status: 'PENDING' }
    // The secret does not match: another browser, or an order created without one
    | { status: 'DENIED' }
    // The single view was already used
    | { status: 'ALREADY_VIEWED'; maskedEmail?: string };

export type ResendResult = 'ACCEPTED' | 'TOO_MANY_RESENDS';

export const ticketService = {
    // Strict single view: the first successful call consumes it. The secret goes in the body, never in the URL
    async viewOnce(reference: string, purchaseSecret: string): Promise<TicketsViewResult> {
        const response = await fetch(`${TICKETS_URL}/view`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            cache: 'no-store',
            body: JSON.stringify({ reference, purchaseSecret })
        });

        switch (response.status) {
            case 200:
                return { status: 'SHOWN', tickets: await response.json() };
            case 202:
                return { status: 'PENDING' };
            case 403:
                return { status: 'DENIED' };
            case 410: {
                const body = await response.json().catch(() => ({}));
                return { status: 'ALREADY_VIEWED', maskedEmail: body.maskedEmail };
            }
            default:
                throw new Error('No fue posible consultar tus boletas');
        }
    },

    // Always to the purchase email; answers ACCEPTED even for unknown references
    async resend(reference: string): Promise<ResendResult> {
        const response = await fetch(`${TICKETS_URL}/resend`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ reference })
        });

        if (response.status === 429) return 'TOO_MANY_RESENDS';
        if (!response.ok) {
            throw new Error('No fue posible reenviar tus boletas');
        }
        return 'ACCEPTED';
    }
};
